import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReactSelect from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FileSaver from 'file-saver';

//COMPONENTS
import DateSelection from '../components/DateSelection';
import TableLogData from '../components/TableLogData';
// REDUX
import { setCurrentPage } from '../redux/app.slice';
// API
import { queryApi, getServerSelectionApi, updateAiLogsApi } from '../apis/logEngine.api';

// messagesStr = messagesStr.replace(/\\n/g, "__")
// messagesStr = messagesStr.replace(/\(/g, " ");
// messagesStr = messagesStr.replace(/\)/g, " ");
// messagesStr = messagesStr.replace(/\\+/g, "")
// messagesStr = messagesStr.replace(/\\/g, "")
// messagesStr = messagesStr.replace(/\"/g, '') 

// TABLES
// payload
// picked_deployment
// model_request
// raw_model_response
// convai_returned_response
// service_errors
// service_start

// TEST QUERIES
// SELECT * FROM model_request WHERE request_id = '[30af6dc2-74df-4f3a-a0db-f83570d153ca]';
// SELECT * FROM payload WHERE request_id = '[b3dde2f8-6c7d-465b-a3b9-3c773470ec83]';
// SELECT * FROM raw_model_response WHERE request_id = '[b3dde2f8-6c7d-465b-a3b9-3c773470ec83]';
// [b3dde2f8-6c7d-465b-a3b9-3c773470ec83], [6ceaf716-9e79-499a-afae-4e94662d2f71], [9d0fa9cb-c7d4-4ec0-80b5-f6c60b83f036]

// SELECT * FROM payload WHERE timestamp = '2024-06-04T15:17:17.000Z';
// SELECT * FROM payload WHERE timestamp >= '2024-06-04' AND timestamp < '2024-06-04T15:00:00.000Z';

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    color: 'black',
    width: '100%',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '&:hover': {
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    transition: 'box-shadow 200ms ease-in-out',
    minHeight: '38px',
    height: '38px',
  }),
  option: provided => ({
    ...provided,
    color: 'black'
  }),
  singleValue: provided => ({
    ...provided,
    color: 'black'
  })
}

const DEFAULT_QUERY = `SELECT * FROM payload WHERE timestamp >= '2024-06-04' AND timestamp < '2024-06-04T15:00:00.000Z';`;

const LogEngine = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState(new Date('07/01/23'));
    const [endDate, setEndDate] = useState(new Date());
    const [isOpen, setIsOpen] = useState(true); // eslint-disable-line
    const [selectedServer, setSelectedServer] = useState({label: 'staging-us', value: 'http://3.222.250.110'});
    const [ serverArr, setServerSelection ] = useState([]);

    const [ queryString, setQueryString ] = useState(DEFAULT_QUERY);
    const [ queryResponseHTML, setQueryResponseHTML ] = useState(null);
    const [ tableChoicesVisible, setTableChoicesVisible ] = useState(false);
    const [ useAi, setUseAi ] = useState(false);
    const [ isDateSelectionActive, setIsDateSelectionActive ] = useState(false);
    const [ queryHistory, setQueryHistory ] = useState([DEFAULT_QUERY]);
    const [ historyIndex, setHistoryIndex ] = useState(0);

    const { loginStatus  } = useSelector((state) => state.user); // eslint-disable-line
    const { role } = useSelector((state) => state.user.user);
    const { user } = useSelector((state) => state.user, shallowEqual); // eslint-disable-line

    const schemaLink = <a href='/schema' style={{ marginLeft: '10px', marginRight: '25px' }}>schema</a>;
    const documentationLink = <a href='/documentation'>documentation</a>;
  
    useEffect(() => {
        if (loginStatus && role) {
          dispatch(setCurrentPage('logengine'));
        } else {
          dispatch(setCurrentPage('login'));
          navigate('/login', { replace: true });
        }
      }, []);
    useEffect(() => {
        if (loginStatus && role) {
            getServerSelection();
        } 
      }, []);
    
    const getServerSelection = async () => {
        const selectionRes = await getServerSelectionApi();
        // console.log('selectionArr:', selectionRes.data)
        setServerSelection(selectionRes.data);
    }
    
    const setDefaultDates = async (start, end) => {
      await setStartDate(start);
      await setEndDate(end);
    };

    const handleStartDate = (date) => {
        setStartDate(date);
    };

    const handleEndDate = (date) => {
        setEndDate(date);
    };

    const addToHistory = useCallback((query) => {
      setQueryHistory(prev => {
        const newHistory = [query, ...prev.slice(0, 9)];
        setHistoryIndex(0);
        return newHistory;
      });
    }, []);

    const updateQueryStringWithDates = useCallback((query, start, end) => {
      let updatedQuery = query.trim();
      if (updatedQuery.endsWith(';')) {
        updatedQuery = updatedQuery.slice(0, -1);
      }

      const startDateStr = start.toISOString().split('T')[0];
      const endDateStr = end.toISOString().split('T')[0];

      const timestampRegex = /WHERE\s+timestamp\s*(?:>=?|<=?|BETWEEN)\s*'[^']*'(?:\s*AND\s*timestamp\s*(?:>=?|<=?)\s*'[^']*')?/i;
      const newTimestampCondition = `timestamp >= '${startDateStr}' AND timestamp < '${endDateStr}'`;

      const whereIndex = updatedQuery.toUpperCase().indexOf('WHERE');

      if (whereIndex !== -1) {
        // There's an existing WHERE clause
        if (timestampRegex.test(updatedQuery)) {
          // Replace existing timestamp condition
          updatedQuery = updatedQuery.replace(timestampRegex, `WHERE ${newTimestampCondition}`);
        } else {
          // Add new timestamp condition to existing WHERE clause
          updatedQuery = updatedQuery.slice(0, whereIndex + 5) + 
            ` ${newTimestampCondition} AND` + 
            updatedQuery.slice(whereIndex + 5);
        }
      } else {
        // No existing WHERE clause, add it with the timestamp condition
        updatedQuery += ` WHERE ${newTimestampCondition}`;
      }

      // Clean up any double spaces
      updatedQuery = updatedQuery.replace(/\s+/g, ' ');

      return updatedQuery + ';';
    }, []);

    const handleToggleDateSelection = useCallback(() => {
      const newIsDateSelectionActive = !isDateSelectionActive;
      setIsDateSelectionActive(newIsDateSelectionActive);

      if (newIsDateSelectionActive) {
        const timestampRegex = /WHERE\s+timestamp\s*(?:>=?|<=?|BETWEEN)\s*'[^']*'(?:\s*AND\s*timestamp\s*(?:>=?|<=?)\s*'[^']*')?/i;
        if (!timestampRegex.test(queryString)) {
          const updatedQueryString = updateQueryStringWithDates(queryString, startDate, endDate);
          setQueryString(updatedQueryString);
          addToHistory(updatedQueryString);
        }
      }
    }, [isDateSelectionActive, queryString, startDate, endDate, updateQueryStringWithDates, addToHistory]);

    const handleUpdateQueryString = useCallback((value) => {
      let queryStr = value.replace(/DROP/, "SELECT").replace(/GRANT/, "SELECT").replace(/TRUNCATE/, "SELECT");
      queryStr = queryStr.replace(/DELETE/, "SELECT").replace(/INSERT/, "SELECT");
      
      const timestampRegex = /WHERE\s+timestamp\s*(?:>=?|<=?|BETWEEN)\s*'[^']*'(?:\s*AND\s*timestamp\s*(?:>=?|<=?)\s*'[^']*')?/i;
      const existingTimestamp = queryString.match(timestampRegex);
      
      if (existingTimestamp && !timestampRegex.test(queryStr)) {
        const whereIndex = queryStr.toUpperCase().indexOf('WHERE');
        if (whereIndex !== -1) {
          queryStr = queryStr.slice(0, whereIndex + 5) + existingTimestamp[0].slice(6) + ' AND ' + queryStr.slice(whereIndex + 5);
        } else {
          queryStr += ' ' + existingTimestamp[0];
        }
      }
      
      setQueryString(queryStr);
      addToHistory(queryStr);
    }, [queryString, addToHistory]);

    const handleDateChange = useCallback((start, end) => {
      setStartDate(start);
      setEndDate(end);
      if (isDateSelectionActive) {
        const updatedQueryString = updateQueryStringWithDates(queryString, start, end);
        setQueryString(updatedQueryString);
        addToHistory(updatedQueryString);
      }
    }, [isDateSelectionActive, queryString, updateQueryStringWithDates, addToHistory]);

    const handleResetQuery = useCallback(() => {
      setQueryString(DEFAULT_QUERY);
      addToHistory(DEFAULT_QUERY);
    }, [addToHistory]);

    const handleBackQuery = useCallback(() => {
      if (historyIndex < queryHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setQueryString(queryHistory[newIndex]);
      }
    }, [historyIndex, queryHistory]);

    const handleServerSelection = (selectedOption) => {
        // console.log('selectedOption:', selectedOption)
        setSelectedServer(selectedOption);
    }

    const handleUpdateAiLogs = async () => {
      if (selectedServer != null) {
        const result = await updateAiLogsApi(user.idToken, selectedServer);
        // console.log('handleUpdateAiLogs:', result.data)
        if (result.data && result.data.err != null) {
          toast(`Update Logs FAILURE: ${result.data}`)
        } else {
          toast(`Ai Logs Updating`)
        }
      } else {
        toast('You must select a server first!');
      }
    }

    const handleQueryLogs = async () => {
      await setQueryResponseHTML('fetching');
      
      const selection = {
        startDate,
        endDate
      }
      
      let queryStringWithSelection = queryString;
      console.log('--- selectedServer:', selectedServer); 
      console.log('--- queryStringWithSelection:', queryStringWithSelection);
      let queryResponse = null;
      if (useAi) {
        queryResponse = await queryApi(selectedServer, queryStringWithSelection, useAi, selection);
      } else {
        queryResponse = await queryApi(selectedServer, queryStringWithSelection, useAi, selection);
      }
      
      console.log('queryString, queryResponse:', queryStringWithSelection, queryResponse.data);
      setQueryResponseHTML(queryResponse.data);
    }

    const RawDataComponent = () => {
      console.log('RawDataComponent queryResponseHTML:', queryResponseHTML)
       
      if (queryResponseHTML === null) {
        return <div className="content raw-data-component">
          {useAi ? "Ready for AI queries.." : "Ready for queries.."}
        </div>
      } else if (queryResponseHTML === 'fetching') {
        return <div className="content raw-data-component">..fetching data from the DB</div>
      } else if (queryResponseHTML.err != null) {
        return <div className="content raw-data-component">{queryResponseHTML.err}</div>
      } else if (useAi) {
        return <div className="content raw-data-component" dangerouslySetInnerHTML={{__html: JSON.stringify(queryResponseHTML)}}></div>
      } else {
        return (
        <div className="content raw-data-component">
          <div>SERVER: {queryResponseHTML && JSON.stringify(queryResponseHTML.server)}</div>
          <div>QUERY: {queryResponseHTML && JSON.stringify(queryResponseHTML.query)}</div>
          <div>DATA: {queryResponseHTML && JSON.stringify(queryResponseHTML.data)}</div>
        </div>
        )
      }
    }

    const handleSaveHTML = async () => {
      const blob = new Blob([queryResponseHTML], { type: 'text/html' });
      FileSaver.saveAs(blob, `queryResponseHTML_${new Date()}.html`);
    }
    const handleSaveJSON = async () => {
      const blob = new Blob([JSON.stringify(queryResponseHTML)], { type: 'application/json' });
      FileSaver.saveAs(blob, `queryResponseHTML_${new Date()}.json`);
    }

    const TableChoices = () => {
      //'table options: model_request, convai_returned_response, picked_deployment, payload'
      return (
        <ul style={{ minWidth: '61.5%', marginLeft: '50px' }}>
          <li>payload</li>
          <li>picked_deployment</li>
          <li>model_request</li>
          <li>raw_model_response</li>
          <li>convai_returned_response</li>
          <li>service_errors</li>
          <li>service_start</li>
          <li>SELECT * FROM payload WHERE timestamp &gt;= '2024-06-04' AND timestamp &lt; '2024-06-04T15:00:00.000Z';</li>
          <li>SELECT * FROM raw_model_response WHERE request_id = '[b3dde2f8-6c7d-465b-a3b9-3c773470ec83]';</li>
        </ul>
      )
    }

    const handleUpdateUseAi = () => {
      console.log('handleUpdateUseAi:', useAi)
      if (useAi) {
        setUseAi(false);
        setQueryString(`SELECT * FROM model_request WHERE request_id = '[30af6dc2-74df-4f3a-a0db-f83570d153ca]';`);
      } else {
        setUseAi(true);
        setIsDateSelectionActive(false);
        setQueryString('Give me last response returned to user with id user_124');
      }
    }

    const handleUseSelection = () => {
      setIsDateSelectionActive((pre) => setIsDateSelectionActive(!pre));
    }

    return (
        <div className="bg-gray-100 min-h-screen p-6">
          <div className="space-y-6">
            <header>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">Log Engine</h1>
            </header>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-wrap items-end gap-4 mb-4">
                <div className="flex-grow min-w-[200px] max-w-[300px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Server</label>
                  <ReactSelect
                    isOpen={isOpen}
                    value={selectedServer}
                    options={serverArr}
                    onChange={handleServerSelection}
                    styles={customStyles}
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={handleToggleDateSelection}
                    className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                      isDateSelectionActive
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                  >
                    {isDateSelectionActive ? 'Date Selection Active' : 'Activate Date Selection'}
                  </button>
                  {isDateSelectionActive && (
                    <div className="absolute left-full ml-2 top-0 z-10">
                      <DateSelection
                        id="date-selection"
                        start={startDate}
                        end={endDate}
                        setDefaultDates={setDefaultDates}
                        handleStartDate={handleStartDate}
                        handleEndDate={handleEndDate}
                        handleDateChange={handleDateChange}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Query</label>
                <div className="flex items-center space-x-2 mb-2">
                  <textarea 
                    rows="5" 
                    className="flex-grow p-2 border rounded-md" 
                    value={queryString} 
                    placeholder='Enter query text here' 
                    onChange={(e) => handleUpdateQueryString(e.target.value)}
                  />
                  <div className="flex flex-col space-y-2">
                    <button 
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
                      onClick={handleQueryLogs}
                    >
                      Fetch
                    </button>
                    <button 
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors duration-200"
                      onClick={handleResetQuery}
                    >
                      Reset Query
                    </button>
                    <button 
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors duration-200"
                      onClick={handleBackQuery}
                      disabled={historyIndex === queryHistory.length - 1}
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button 
                    className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                      useAi
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                    onClick={handleUpdateUseAi}
                  >
                    AI {useAi ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div className="flex space-x-2">
                  <button 
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200"
                    onClick={handleSaveJSON}
                  >
                    Download Raw
                  </button>
                  <button 
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200"
                    onClick={handleUpdateAiLogs}
                  >
                    Update Logs
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Query Response</h2>
              {tableChoicesVisible && <TableChoices />}
              {!tableChoicesVisible && <RawDataComponent />}
            </div>
          </div>
        </div>
    );
};

export default LogEngine;
